import { createFileRoute, Link } from '@tanstack/react-router'
import { isPermissionDeniedError } from '@/lib/error-reporting'
import { z } from 'zod'
import { useMemo, useState } from 'react'
import { FileText, ArrowUpRight, MapPin, Pencil, Loader2 } from 'lucide-react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { getInvoiceDetailQueryOptions, useInvoice } from '@/stores/invoices.store'
import { useContact } from '@/stores/contacts.store'
import { useBooking } from '@/stores/bookings.store'
import { useEventsList, useEvent } from '@/stores/events.store'
import { useNotes } from '@/stores/notes.store'
import { useAttachments } from '@/stores/attachments.store'
import { useTasksList } from '@/stores/tasks.store'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { ContactHeader } from '@/components/events'
import { EventSheet } from '@/components/events/id/event-sheet'
import { CopyButton } from '@/lib/clipboard-utils'
import { AccessDeniedState } from '@/components/shared/access-denied-state'
import { ActivityTabs } from '@/components/shared/activity-tabs'
import { InvoiceWorkspace } from '@/components/invoices/invoice-workspace'
import { LinkedRecordCard } from '@/components/shared/financial-document'
import { seedInvoiceDetailCaches } from '@/lib/detail-seeding'
import { connectTransport } from '@/lib/connect-transport'
import { isValidUUID } from '@/lib/uuid'
import { canPreloadOrganizationQueries } from '@/lib/route-preloading'

export const Route = createFileRoute('/_app/_home/invoices/$id')({
  component: InvoiceDetailPage,
  validateSearch: z.object({
    from: z.string().optional(),
  }),
  loader: async ({ context, params }) => {
    if (!isValidUUID(params.id) || !canPreloadOrganizationQueries(context.auth)) return
    const { queryClient } = context

    // Resolve the aggregate before mounting entity hooks so cold navigation
    // cannot race the individual invoice/contact/booking queries.
    const opts = getInvoiceDetailQueryOptions(params.id)
    try {
      const resp = await queryClient.ensureQueryData(opts)
      const updatedAt = queryClient.getQueryState(opts.queryKey)?.dataUpdatedAt ?? Date.now()
      seedInvoiceDetailCaches(queryClient, connectTransport, resp, updatedAt)
    } catch {
      // The component's entity queries provide the user-visible error state.
    }
  },
})

function InvoiceDetailPage() {
  const { id } = Route.useParams()
  const { from } = Route.useSearch()

  // Memoize breadcrumbs to prevent infinite loops
  const customBreadcrumbs = useMemo(() => {
    if (from === 'events') {
      return [
        { label: 'Home', href: '/home' },
        { label: 'Events', href: '/events' },
        { label: 'Invoice' },
      ]
    }
    // Default: from invoices list page
    return undefined
  }, [from])

  // Set breadcrumbs based on navigation source
  useBreadcrumbFromRoute(customBreadcrumbs)

  // Data fetching
  const {
    invoice,
    isLoading: isInvoiceLoading,
    isError: isInvoiceError,
    error: invoiceError,
    refetch,
  } = useInvoice(id)
  const { contact } = useContact(invoice?.contactId)
  const { booking } = useBooking(invoice?.bookingId || undefined)
  // Fetch notes and tasks for the invoice
  const { notes } = useNotes({
    parentType: ParentTypeEnum.PARENT_TYPE_INVOICE,
    parentId: id,
  })

  const { tasks } = useTasksList({
    parentType: ParentTypeEnum.PARENT_TYPE_INVOICE,
    parentId: id,
  })

  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_INVOICE,
    parentId: id,
  })

  // Gate the full-page skeleton on the invoice alone. The ContactHeader area
  // degrades gracefully while the contact resolves — and after the loader's
  // aggregate seeding it is a cache-hit anyway.
  const isLoading = isInvoiceLoading
  const isError = isInvoiceError

  // Loading skeleton
  if (isLoading) {
    return <InvoiceDetailSkeleton />
  }

  // A 403 here is a role denial, not a failure — say so instead of implying the
  // invoice is broken. Every other error keeps the generic state.
  if (isPermissionDeniedError(invoiceError)) {
    return (
      <div className="flex h-full flex-col p-4">
        <AccessDeniedState variant="page" noun="invoices" />
      </div>
    )
  }

  // Error state
  if (isError || !invoice) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="flex-1 border border-dashed py-12">
          <EmptyMedia variant="icon">
            <FileText className="h-6 w-6" />
          </EmptyMedia>
          <EmptyDescription>We couldn't load this invoice right now.</EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4 pb-8">
      {/* Contact Header */}
      <div className="flex items-start justify-between gap-4">
        <ContactHeader
          firstName={contact?.firstName}
          lastName={contact?.lastName}
          email={contact?.email}
          phoneNumber={contact?.phoneNumber}
          isArchived={!!contact?.archivedAt}
        />
        {booking && booking.eventIds.length > 0 && (
          <Button variant="outline" size="sm" asChild>
            <Link to="/events/$id" params={{ id: booking.eventIds[0] }}>
              View Event
              <ArrowUpRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        )}
      </div>

      <InvoiceWorkspace
        invoice={invoice}
        recipientName={[contact?.firstName, contact?.lastName].filter(Boolean).join(' ')}
        refetch={refetch}
        main={
          <ActivityTabs
            organizationId={invoice.organizationId}
            parentType={ParentTypeEnum.PARENT_TYPE_INVOICE}
            parentId={invoice.id}
            notes={notes}
            tasks={tasks}
            attachments={attachments}
            customerEmail={contact?.email}
            emailContactId={invoice.contactId}
          />
        }
        aside={
          <>
            <EventLocationCard bookingId={booking?.id} />

            <LinkedRecordCard
              title="Linked Records"
              rows={[
                {
                  label: 'Booking',
                  value: booking?.eventIds.length ? `${booking.eventIds.length} event${booking.eventIds.length === 1 ? '' : 's'}` : 'Booking context',
                  action: booking?.eventIds[0] ? (
                    <Button variant="ghost" size="icon" asChild>
                      <Link to="/events/$id" params={{ id: booking.eventIds[0] }} aria-label="Open linked event">
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  ) : undefined,
                },
                {
                  label: 'Created from Quote',
                  value: invoice.quoteId ? 'Source quote' : 'Standalone invoice',
                  action: invoice.quoteId ? (
                    <Button variant="ghost" size="icon" asChild>
                      <Link to="/quotes/$id" params={{ id: invoice.quoteId }} aria-label="Open source quote">
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  ) : undefined,
                },
              ]}
            />
          </>
        }
      />
    </div>
  )
}

/**
 * Displays the event location(s) for the invoice's booking, with an edit
 * affordance that reuses the existing event-sheet in update mode. The event's
 * own `eventLocation` is the source of truth, so this reads from the events
 * list rather than the booking.
 * Renders one row per event even when its location is unset, so there's
 * always a pencil to set an address — only skipped when there are no events
 * to edit at all.
 */
function EventLocationCard({ bookingId }: { bookingId?: string }) {
  const [editingEventId, setEditingEventId] = useState<string | undefined>(undefined)
  const { events } = useEventsList({ bookingId, enabled: !!bookingId })
  const { event: editingEvent } = useEvent(editingEventId ?? '')

  if (events.length === 0) return null

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {events.length === 1 ? 'Event Location' : 'Event Locations'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {events.map((event, index) => {
            const location = event.eventLocation?.trim()
            return (
              <div key={event.id} className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    {events.length > 1 && (
                      <p className="text-xs text-muted-foreground">Event {index + 1}</p>
                    )}
                    <div className="flex items-start gap-2">
                      {location ? (
                        <>
                          <span className="min-w-0 flex-1 break-words text-sm font-medium">{location}</span>
                          <CopyButton value={location} ariaLabel="Copy location" />
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground">No location set</span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => setEditingEventId(event.id)}
                  disabled={editingEventId === event.id && !editingEvent}
                >
                  {editingEventId === event.id && !editingEvent ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Pencil className="h-4 w-4" />
                  )}
                  <span className="sr-only">Edit event location</span>
                </Button>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {editingEventId && editingEvent && (
        <EventSheet
          key={editingEvent.id}
          mode="update"
          bookingId={editingEvent.bookingId}
          event={editingEvent}
          open={true}
          onOpenChange={(open) => { if (!open) setEditingEventId(undefined) }}
        />
      )}
    </>
  )
}

/**
 * Loading skeleton for the invoice detail page
 */
function InvoiceDetailSkeleton() {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {/* Header Skeleton */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <SkeletonBar className="h-8 w-48" />
            <SkeletonBar className="h-4 w-32" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <SkeletonBar className="h-4 w-4" />
            <SkeletonBar className="h-4 w-40" />
            <SkeletonBar className="h-6 w-6 rounded" />
          </div>
          <div className="flex items-center gap-2">
            <SkeletonBar className="h-4 w-4" />
            <SkeletonBar className="h-4 w-32" />
            <SkeletonBar className="h-6 w-6 rounded" />
          </div>
        </div>
      </div>

      {/* Two-column skeleton */}
      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        {/* Sidebar skeleton */}
        <div className="space-y-4">
          {/* Summary card skeleton */}
          <Card>
            <CardHeader>
              <SkeletonBar className="h-6 w-24" />
            </CardHeader>
            <CardContent className="space-y-4">
              <SkeletonBar className="h-8 w-32" />
              <SkeletonBar className="h-10 w-full" />
              <div className="space-y-2">
                <SkeletonBar className="h-3 w-full" />
                <div className="grid grid-cols-2 gap-3">
                  <SkeletonBar className="h-16 w-full rounded-lg" />
                  <SkeletonBar className="h-16 w-full rounded-lg" />
                </div>
              </div>
              <div className="space-y-2 pt-2">
                <SkeletonBar className="h-4 w-full" />
                <SkeletonBar className="h-4 w-full" />
                <SkeletonBar className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>

          {/* Actions skeleton */}
          <Card>
            <CardHeader>
              <SkeletonBar className="h-4 w-24" />
            </CardHeader>
            <CardContent className="space-y-2">
              <SkeletonBar className="h-10 w-full" />
              <div className="grid grid-cols-2 gap-2">
                <SkeletonBar className="h-8 w-full" />
                <SkeletonBar className="h-8 w-full" />
                <SkeletonBar className="h-8 w-full" />
                <SkeletonBar className="h-8 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main content skeleton */}
        <div className="space-y-4">
          {/* Status banner skeleton */}
          <SkeletonBar className="h-24 w-full rounded-lg" />

          {/* Line items skeleton */}
          <Card>
            <CardHeader>
              <SkeletonBar className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <SkeletonBar className="h-4 flex-1" />
                  <SkeletonBar className="h-4 w-16" />
                  <SkeletonBar className="h-4 w-20" />
                </div>
              ))}
              <div className="border-t pt-3 space-y-2">
                <SkeletonBar className="h-4 w-32 ml-auto" />
                <SkeletonBar className="h-6 w-40 ml-auto" />
              </div>
            </CardContent>
          </Card>

          {/* Payment schedules skeleton */}
          <Card>
            <CardHeader>
              <SkeletonBar className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <SkeletonBar key={i} className="h-20 w-full rounded-lg" />
              ))}
            </CardContent>
          </Card>

          {/* Payment history skeleton */}
          <Card>
            <CardHeader>
              <SkeletonBar className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              <SkeletonBar className="h-4 w-24" />
              {Array.from({ length: 2 }).map((_, i) => (
                <SkeletonBar key={i} className="h-20 w-full rounded-lg" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
